import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, XIcon, PlusIcon, Trash2Icon, GripVerticalIcon, ChevronUpIcon, ChevronDownIcon, XCircleIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile, useIsTablet } from '../hooks/useMediaQuery';
interface Section {
  id: string;
  title: string;
  placeholder: string;
}
export function ReportTemplateCreate() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [nome, setNome] = useState('');
  const [tipoEsame, setTipoEsame] = useState('Risonanza Magnetica');
  const [standard, setStandard] = useState(false);
  const [descrizione, setDescrizione] = useState('');
  const [sections, setSections] = useState<Section[]>([{
    id: '1',
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...'
  }, {
    id: '2',
    title: 'Tecnica',
    placeholder: 'Descrivere la tecnica...'
  }, {
    id: '3',
    title: 'Reperti',
    placeholder: 'Descrivere i reperti...'
  }, {
    id: '4',
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni...'
  }]);
  const [toastMessage, setToastMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Drag & Drop State
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({
      type,
      text
    });
    setTimeout(() => setToastMessage(null), 3000);
  };
  const handleAddSection = () => {
    const newId = Date.now().toString();
    setSections([{
      id: newId,
      title: 'Nuova sezione',
      placeholder: ''
    }, ...sections]);
    showToast('success', 'Sezione aggiunta correttamente.');
    setTimeout(() => {
      const el = document.getElementById(`section-title-${newId}`);
      if (el) el.focus();
    }, 100);
  };
  const handleRemoveSection = (id: string) => {
    setSections(sections.filter(s => s.id !== id));
  };
  const handleUpdateSection = (id: string, field: keyof Section, value: string) => {
    setSections(sections.map(s => s.id === id ? {
      ...s,
      [field]: value
    } : s));
    if (errors[`section-${id}`] && field === 'title') {
      const newErrors = {
        ...errors
      };
      delete newErrors[`section-${id}`];
      setErrors(newErrors);
    }
  };
  const moveSection = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index > 0) {
      const newSections = [...sections];
      const temp = newSections[index];
      newSections[index] = newSections[index - 1];
      newSections[index - 1] = temp;
      setSections(newSections);
    } else if (direction === 'down' && index < sections.length - 1) {
      const newSections = [...sections];
      const temp = newSections[index];
      newSections[index] = newSections[index + 1];
      newSections[index + 1] = temp;
      setSections(newSections);
    }
  };
  // Drag & Drop Handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(img, 0, 0);
  };
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };
  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      handleDragEnd();
      return;
    }
    const newSections = [...sections];
    const draggedItem = newSections[draggedIndex];
    newSections.splice(draggedIndex, 1);
    newSections.splice(dropIndex, 0, draggedItem);
    setSections(newSections);
    handleDragEnd();
  };
  const handleSave = () => {
    const newErrors: Record<string, string> = {};
    if (!nome.trim()) newErrors.nome = 'Il nome del modello è obbligatorio';
    if (!tipoEsame.trim()) newErrors.tipoEsame = 'Il tipo di esame è obbligatorio';
    sections.forEach(s => {
      if (!s.title.trim()) {
        newErrors[`section-${s.id}`] = 'Il titolo della sezione è obbligatorio';
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast('error', 'Compilare tutti i campi obbligatori');
      return;
    }
    // Simulate save
    navigate('/templates');
  };
  return <div className="space-y-6 w-full min-w-0 pb-12" data-id="element-2194">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2195">
        <div className="flex items-start gap-3" data-id="element-2196">
          <button onClick={() => navigate('/templates')} className="p-2 hover:bg-clinical-gray rounded-full transition-colors text-text-secondary mt-0.5" data-id="element-2197">
            <ArrowLeftIcon size={20} data-id="element-2198" />
          </button>
          <div data-id="element-2199">
            <h1 className="text-2xl md:text-page-title text-text-primary font-bold" data-id="element-2200">
              Crea Nuovo Modello
            </h1>
            <p className="text-sm text-text-secondary mt-0.5" data-id="element-2201">
              Configura le informazioni e la struttura del nuovo modello di
              referto.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 pl-11 md:pl-0" data-id="element-2202">
          <button onClick={() => navigate('/templates')} className="medical-btn-secondary py-2 px-3 text-sm flex-1 md:flex-none justify-center" title="Annulla" data-id="element-2203">
            <XIcon size={16} className={isTablet ? '' : 'mr-1.5'} data-id="element-2204" />
            {!isTablet && ' Annulla'}
          </button>
          <button onClick={handleSave} className="medical-btn-primary py-2 px-3 text-sm flex-1 md:flex-none justify-center" title="Salva modello" data-id="element-2205">
            <CheckCircleIcon size={16} className={isTablet ? '' : 'mr-1.5'} data-id="element-2206" />
            {!isTablet && ' Salva modello'}
          </button>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 p-5 md:p-6" data-id="element-2207">
        <h3 className="text-table-header text-text-secondary uppercase tracking-wider mb-4" data-id="element-2208">
          Informazioni Generali
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2209">
          <div className="md:col-span-2" data-id="element-2210">
            <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2211">
              Nome modello *
            </label>
            <input type="text" value={nome} onChange={e => {
            setNome(e.target.value);
            if (errors.nome) setErrors({
              ...errors,
              nome: ''
            });
          }} placeholder="Es. Referto RM Ginocchio Standard" className={`medical-input ${errors.nome ? 'border-status-error ring-1 ring-status-error/20' : ''}`} data-id="element-2212" />
            {errors.nome && <p className="text-xs text-status-error mt-1" data-id="element-2213">{errors.nome}</p>}
          </div>
          <div data-id="element-2214">
            <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2215">
              Tipo esame associato *
            </label>
            <select value={tipoEsame} onChange={e => {
            setTipoEsame(e.target.value);
            if (errors.tipoEsame) setErrors({
              ...errors,
              tipoEsame: ''
            });
          }} className={`medical-input ${errors.tipoEsame ? 'border-status-error ring-1 ring-status-error/20' : ''}`} data-id="element-2216">
              <option value="" data-id="element-2217">Seleziona un tipo di esame</option>
              <option value="Risonanza Magnetica" data-id="element-2218">Risonanza Magnetica</option>
              <option value="TAC" data-id="element-2219">TAC</option>
              <option value="Ecografia" data-id="element-2220">Ecografia</option>
              <option value="Radiografia" data-id="element-2221">Radiografia</option>
              <option value="Mammografia" data-id="element-2222">Mammografia</option>
            </select>
            {errors.tipoEsame && <p className="text-xs text-status-error mt-1" data-id="element-2223">
                {errors.tipoEsame}
              </p>}
          </div>
          <div data-id="element-2224">
            <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2225">
              Tipologia
            </label>
            <select value={standard ? 'standard' : 'personalizzato'} onChange={e => setStandard(e.target.value === 'standard')} className="medical-input" data-id="element-2226">
              <option value="standard" data-id="element-2227">
                Standard (Globale per tutti i medici)
              </option>
              <option value="personalizzato" data-id="element-2228">
                Personalizzato (Solo per te)
              </option>
            </select>
          </div>
          <div className="md:col-span-2" data-id="element-2229">
            <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2230">
              Descrizione / Note
            </label>
            <textarea value={descrizione} onChange={e => setDescrizione(e.target.value)} placeholder="Breve descrizione dell'utilizzo di questo modello..." className="medical-input min-h-[80px] resize-y" data-id="element-2231" />
          </div>
        </div>
      </div>

      {/* Structure Builder */}
      <div className="bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 p-5 md:p-6" data-id="element-2232">
        <div className="flex items-center justify-between mb-4" data-id="element-2233">
          <h3 className="text-table-header text-text-secondary uppercase tracking-wider" data-id="element-2234">
            Struttura del Referto
          </h3>
          <button onClick={handleAddSection} className="text-sm font-medium text-medical-green hover:text-medical-green/80 flex items-center gap-1" data-id="element-2235">
            <PlusIcon size={16} data-id="element-2236" /> Aggiungi sezione
          </button>
        </div>

        <div className="space-y-4" data-id="element-2237">
          {sections.map((section, index) => <div key={section.id} className="relative" data-id="element-2238">
              {dragOverIndex === index && draggedIndex !== null && draggedIndex !== index && <div className="absolute -top-2 left-0 right-0 h-1 bg-medical-green rounded-full z-10" data-id="element-2239" />}
              <div className={`border border-clinical-gray-200 rounded-medical-lg bg-clinical-gray-50 flex overflow-hidden transition-opacity ${draggedIndex === index ? 'opacity-50' : 'opacity-100'}`} onDragOver={e => handleDragOver(e, index)} onDrop={e => handleDrop(e, index)} onDragEnd={handleDragEnd} data-id="element-2240">
                {/* Drag Handle / Reorder */}
                <div className="w-10 bg-clinical-gray-100 border-r border-clinical-gray-200 flex flex-col items-center justify-center gap-2 py-2 shrink-0" data-id="element-2241">
                  <button onClick={() => moveSection(index, 'up')} disabled={index === 0} className="text-text-tertiary hover:text-text-primary disabled:opacity-30" data-id="element-2242">
                    <ChevronUpIcon size={16} data-id="element-2243" />
                  </button>
                  <div draggable onDragStart={e => handleDragStart(e, index)} className="cursor-grab active:cursor-grabbing p-1" data-id="element-2244">
                    <GripVerticalIcon size={16} className="text-text-tertiary" data-id="element-2245" />
                  </div>
                  <button onClick={() => moveSection(index, 'down')} disabled={index === sections.length - 1} className="text-text-tertiary hover:text-text-primary disabled:opacity-30" data-id="element-2246">
                    <ChevronDownIcon size={16} data-id="element-2247" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 p-4 space-y-3" data-id="element-2248">
                  <div className="flex items-start justify-between gap-4" data-id="element-2249">
                    <div className="flex-1" data-id="element-2250">
                      <input id={`section-title-${section.id}`} type="text" value={section.title} onChange={e => handleUpdateSection(section.id, 'title', e.target.value)} placeholder="Titolo sezione (es. Reperti) *" className={`w-full bg-transparent text-sm font-semibold text-text-primary border-b hover:border-clinical-gray-300 focus:border-medical-green focus:outline-none px-1 py-0.5 transition-colors ${errors[`section-${section.id}`] ? 'border-status-error' : 'border-transparent'}`} data-id="element-2251" />
                      {errors[`section-${section.id}`] && <p className="text-xs text-status-error mt-1 px-1" data-id="element-2252">
                          {errors[`section-${section.id}`]}
                        </p>}
                    </div>
                    <button onClick={() => handleRemoveSection(section.id)} className="p-1.5 text-text-tertiary hover:text-status-error hover:bg-status-error-bg rounded transition-colors shrink-0" title="Elimina sezione" data-id="element-2253">
                      <Trash2Icon size={16} data-id="element-2254" />
                    </button>
                  </div>
                  <div data-id="element-2255">
                    <textarea value={section.placeholder} onChange={e => handleUpdateSection(section.id, 'placeholder', e.target.value)} placeholder="Contenuto predefinito o placeholder..." className="medical-input text-sm min-h-[80px] resize-y bg-white" data-id="element-2256" />
                  </div>
                </div>
              </div>
            </div>)}

          {sections.length === 0 && <div className="text-center py-8 border-2 border-dashed border-clinical-gray-200 rounded-medical-lg" data-id="element-2257">
              <p className="text-sm text-text-secondary mb-2" data-id="element-2258">
                Nessuna sezione presente.
              </p>
              <button onClick={handleAddSection} className="medical-btn-secondary text-sm" data-id="element-2259">
                <PlusIcon size={16} className="mr-1.5" data-id="element-2260" /> Aggiungi la prima
                sezione
              </button>
            </div>}
        </div>
      </div>

      {/* Toast */}
      <AnimatePresence data-id="element-2261">
        {toastMessage && <motion.div initial={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} className={`fixed bottom-6 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-medical-lg shadow-modal border bg-white w-[calc(100%-3rem)] sm:w-auto ${toastMessage.type === 'success' ? 'border-status-success/20' : 'border-status-error/20'}`} data-id="element-2262">
            {toastMessage.type === 'success' ? <CheckCircleIcon className="text-status-success shrink-0" size={20} data-id="element-2263" /> : <XCircleIcon className="text-status-error shrink-0" size={20} data-id="element-2264" />}
            <span className="text-sm font-medium text-text-primary" data-id="element-2265">
              {toastMessage.text}
            </span>
          </motion.div>}
      </AnimatePresence>
    </div>;
}